import type { RuleModule } from '../engine/registry';
import { OK, reject, type GameEventDraft, type GameRuntime, type GameState } from '../engine/state';
import { enterPhase, openWindow } from '../engine/phase';
import { notePiecePlaced } from '../engine/placement';
import { liveBoard } from '../engine/liveBoard';
import type { ResourceBag } from '../engine/gameDef';
import type { SeatIdx } from '../types';

// The Homestead phase — the snake draft (outline §4; plan §7.2): round 1 in turn order
// from firstSeat, round 2 reversed (last placer goes first); each placement is settlement
// THEN its adjacent road (two atomic log entries — brief §6.1); the SECOND settlement pays
// starting resources on placement; distance rule binds from the very first placement,
// own buildings and neutral blockers included (outline §14.11).

/**
 * The snake advance after a seat's setup road (outline §4): positions counted from
 * firstSeat, resigned seats skipped; round 1 runs clockwise, round 2 back again, and the
 * last round-2 placement opens the first PRE_ROLL. Exported (H20-S0b) so a module that
 * replaces SETUP_ROAD — or adds a second setup verb — advances the draft by the SAME rule.
 */
export function advanceSetupSnake(state: GameState, env: GameRuntime, seat: SeatIdx, round: 1 | 2, emit: (e: GameEventDraft) => void): void {
  const n = state.seats.length;
  const first = state.turn.firstSeat;
  const pos = (s: number): number => (s - first + n) % n;
  const seatAt = (p: number): number => (first + p + n) % n;
  const live = (s: number): boolean => !state.seats[s]!.resigned;
  if (round === 1) {
    let p = pos(seat) + 1;
    while (p < n && !live(seatAt(p))) p += 1;
    if (p < n) enterPhase(state, env, { t: 'SETUP_SETTLEMENT', seat: seatAt(p), round: 1 }, emit);
    else enterPhase(state, env, { t: 'SETUP_SETTLEMENT', seat, round: 2 }, emit); // last placer goes first, reversed
  } else {
    let p = pos(seat) - 1;
    while (p >= 0 && !live(seatAt(p))) p -= 1;
    if (p >= 0) enterPhase(state, env, { t: 'SETUP_SETTLEMENT', seat: seatAt(p), round: 2 }, emit);
    else {
      emit({ kind: 'TURN_STARTED', vis: 'public', seat: state.turn.roller, turnNumber: state.turn.number });
      enterPhase(state, env, { t: 'PRE_ROLL', seat: state.turn.roller }, emit); // outline §4: round-1 first player rolls
      openWindow(state, env, state.turn.roller);
    }
  }
}

/**
 * Starting resources for a SECOND settlement (outline §4): one per adjacent PRODUCING hex,
 * Supply-short paying min(owed, stock) per type (D4). Exported (H20) so a module replacing
 * SETUP_SETTLEMENT pays by the same rule — it reads the live board, which is env.board itself
 * in every classic game.
 */
export function payStartingResources(state: GameState, env: GameRuntime, seat: SeatIdx, vertex: string, emit: (e: GameEventDraft) => void): void {
  const producesOf = new Map(env.def.tiles.map((t) => [t.id, t.produces]));
  const owed: ResourceBag = {};
  const board = liveBoard(state, env);
  for (const hid of board.vertices.get(vertex)!.hexes) {
    const produced = producesOf.get(board.hexes.get(hid)!.tile) ?? null;
    if (produced !== null) owed[produced] = (owed[produced] ?? 0) + 1;
  }
  const paid: ResourceBag = {};
  for (const [rid, n] of Object.entries(owed)) {
    const grant = Math.min(n, state.supply[rid] ?? 0);
    if (grant > 0) {
      paid[rid] = grant;
      state.supply[rid] = (state.supply[rid] ?? 0) - grant;
      state.seats[seat]!.hand[rid] = (state.seats[seat]!.hand[rid] ?? 0) + grant;
    }
  }
  emit({ kind: 'STARTING_RESOURCES', vis: 'public', seat, bag: paid });
}

export const setupDraftModule: RuleModule = {
  id: 'setup-draft',
  register(ctx) {
    ctx.phaseState('SETUP_SETTLEMENT', { eligibleWinner: () => null, timeoutKey: 'SETUP_SETTLEMENT' });
    ctx.phaseState('SETUP_ROAD', { eligibleWinner: () => null, timeoutKey: 'SETUP_ROAD' });

    ctx.action('SETUP_SETTLEMENT', {
      enumerate(state, env, seat) {
        if (state.phase.t !== 'SETUP_SETTLEMENT' || state.phase.seat !== seat) return null;
        return [...env.board.vertices.keys()].map((vertex) => ({ vertex }));
      },
      validate(state, action, env) {
        if (state.phase.t !== 'SETUP_SETTLEMENT') return reject('WRONG_PHASE');
        if (state.phase.seat !== action.seat) return reject('NOT_YOUR_TURN');
        const vertex = action.payload['vertex'];
        if (typeof vertex !== 'string') return reject('BAD_PAYLOAD');
        const v = env.board.vertices.get(vertex);
        if (!v) return reject('NO_SUCH_SITE');
        if (state.board.vertices[vertex]) return reject('SITE_OCCUPIED');
        for (const nb of v.neighbors) {
          if (state.board.vertices[nb]) return reject('DISTANCE_RULE'); // any building — own or neutral included
        }
        return OK;
      },
      apply(state, action, env, emit) {
        if (state.phase.t !== 'SETUP_SETTLEMENT') return; // validated
        const seat = action.seat;
        const round = state.phase.round;
        const vertex = String(action.payload['vertex']);
        state.board.vertices[vertex] = { piece: 'settlement', owner: seat };
        emit({ kind: 'SETUP_PLACED', vis: 'public', seat, piece: 'settlement', at: vertex, round });
        notePiecePlaced(state, env, seat, 'settlement', vertex, emit);
        if (round === 2) payStartingResources(state, env, seat, vertex, emit);
        enterPhase(state, env, { t: 'SETUP_ROAD', seat, round, at: vertex }, emit);
      },
    });

    ctx.action('SETUP_ROAD', {
      enumerate(state, env, seat) {
        if (state.phase.t !== 'SETUP_ROAD' || state.phase.seat !== seat) return null;
        return env.board.vertices.get(state.phase.at)!.edges.map((edge) => ({ edge }));
      },
      validate(state, action, env) {
        if (state.phase.t !== 'SETUP_ROAD') return reject('WRONG_PHASE');
        if (state.phase.seat !== action.seat) return reject('NOT_YOUR_TURN');
        const edge = action.payload['edge'];
        if (typeof edge !== 'string') return reject('BAD_PAYLOAD');
        const e = env.board.edges.get(edge);
        if (!e) return reject('NO_SUCH_SITE');
        if (state.board.edges[edge]) return reject('SITE_OCCUPIED');
        if (!e.vertices.includes(state.phase.at)) return reject('ROAD_MUST_TOUCH_SETTLEMENT'); // THIS round's settlement (outline §4)
        return OK;
      },
      apply(state, action, env, emit) {
        if (state.phase.t !== 'SETUP_ROAD') return; // validated
        const seat = action.seat;
        const round = state.phase.round;
        const edge = String(action.payload['edge']);
        state.board.edges[edge] = { piece: 'road', owner: seat };
        emit({ kind: 'SETUP_PLACED', vis: 'public', seat, piece: 'road', at: edge, round });
        notePiecePlaced(state, env, seat, 'road', edge, emit);

        advanceSetupSnake(state, env, seat, round, emit);
      },
    });
  },
};
